<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <main class="unlock-videos">
    <div v-if="loading" class="loading">Loading…</div>
    <div v-else class="unlock-content">
      <h1 class="poster-title">
        <span>UNLOCK</span>
        <img src="/images/logo/vizbi-inline.png" alt="VIZBI" class="vizbi-logo" />
        <span>VIDEOS</span>
      </h1>
      <p class="poster-subtitle">Visualizations transforming the life sciences</p>
      <p v-if="hasWorkshopAccess && !hasMasterclassAccess" class="intro text">
        You already have access to the Workshop videos — register to unlock the Masterclass videos
        and complete your access to the full video library.
      </p>
      <p v-else-if="hasMasterclassAccess && !hasWorkshopAccess" class="intro text">
        You already have access to the Masterclass videos — register to unlock the Workshop videos
        and complete your access to the full video library.
      </p>
      <p
        v-else-if="hasWorkshopAccess && hasMasterclassAccess && isFullyPostEvent"
        class="intro text">
        All currently scheduled 2026 events have concluded. You have access to the full VIZBI video
        library.
      </p>
      <p v-else-if="hasWorkshopAccess && hasMasterclassAccess" class="intro text">
        You have access to the full VIZBI video library.
      </p>
      <!-- after the last November online event (useEventState isFullyPostEvent, keyDates.json) -->
      <p v-else-if="isFullyPostEvent" class="intro text">
        All currently scheduled 2026 events have concluded, but registration remains open, giving
        access to the full video library.
      </p>
      <!-- the library's size, what's free: stats.json "library", "free" (Seán, 2026-10-22) -->
      <p v-else-if="libraryStats" class="intro text">
        Registering for the online Workshop and Masterclass gives access to the full VIZBI video
        library ({{ libraryStats.videos }} videos, {{ libraryStats.hours }} hours).
      </p>
      <p v-else class="intro text">
        Registering for the online Workshop and Masterclass gives access to a wealth of videos in
        the VIZBI library.
      </p>
      <ul class="intro-list">
        <template v-if="showValueSection">
          <li v-if="freeStats">{{ freeStats.videos }} videos are already free for everyone.</li>
          <li v-else>Videos from 2010 to 2016 are free for everyone.</li>
        </template>
        <template v-if="hasWorkshopAccess && hasMasterclassAccess">
          <li>All Workshop videos unlocked ✔</li>
          <li>All Masterclass videos unlocked ✔</li>
        </template>
        <template v-else-if="hasWorkshopAccess">
          <li>All Workshop videos unlocked ✔</li>
          <li>Masterclass registration unlocks all Masterclass videos.</li>
        </template>
        <template v-else-if="hasMasterclassAccess">
          <li>All Masterclass videos unlocked ✔</li>
          <li>Workshop registration unlocks all Workshop videos.</li>
        </template>
        <template v-else>
          <li>Workshop registration unlocks all Workshop videos.</li>
          <li>Masterclass registration unlocks all Masterclass videos.</li>
        </template>
        <li v-if="showInPersonBullet">In-person and virtual registration gives the same access.</li>
      </ul>
      <!-- <p v-if="showValueSection" class="final text">
        We plan further online sessions with the 2026 speakers if there is sufficient demand —
        indicate your interest during registration.
      </p> -->
      <template v-if="showValueSection">
        <p class="value-prop">
          <template v-if="hasWorkshopAccess">Add Masterclass access:</template>
          <template v-else-if="hasMasterclassAccess">Add Workshop access:</template>
          <template v-else>One registration = a wealth of data visualization solutions:</template>
        </p>
        <div class="reg-buttons">
          <a
            v-if="!hasMasterclassAccess"
            :href="registrationUrl('masterclass', '/Videos')"
            class="reg-btn"
            @click.prevent="redirectToRegistration('masterclass', '/Videos')">
            <strong>1</strong>
            Masterclass Registration
            <template v-if="masterclassStats">
              =
              <strong
              >{{ masterclassStats.videos }} videos, {{ masterclassStats.hours }} hours</strong
              >
            </template>
          </a>
          <a
            v-if="!hasWorkshopAccess"
            :href="registrationUrl('workshop', '/Videos')"
            class="reg-btn"
            @click.prevent="redirectToRegistration('workshop', '/Videos')">
            <strong>1</strong>
            Workshop Registration
            <template v-if="workshopStats">
              = <strong>{{ workshopStats.videos }} videos, {{ workshopStats.hours }} hours</strong>
            </template>
          </a>
        </div>
        <!-- under the green buttons, apart from them (2026-10-14): two quiet rows, the full
             width of the text (the buttons are narrower), each a link as a whole -->
        <div class="info-rows">
          <router-link :to="`/${currentYear}/Registration`" class="info-row">
            How much does registration cost?
          </router-link>
          <!-- the email component wraps the whole row: a press anywhere on it writes to us -->
          <Email to="vizbi-contact" class="info-row">For institutional access, contact us</Email>
        </div>
      </template>
      <p class="highlights-label">Highlights from the VIZBI Video Library:</p>
      <section
        v-for="session in sessions"
        :key="session.key"
        class="session"
        :class="{biological: session.bio}">
        <h2 class="session-title">
          {{ session.title }}
          <span v-if="session.total" class="session-count"
          >{{ session.total }} {{ session.countLabel }} including:</span
          >
        </h2>
        <div class="cards">
          <div v-for="s in session.featured" :key="s.name" class="card">
            <!-- opens the speaker dialog here (their latest year); the link is the fallback -->
            <a
              :href="`/${s.year}/People#${s.name.replace(/ /g, '_')}`"
              class="card-link"
              @click.prevent="openSpeaker(s.name, null, s.year)">
              <div class="image-wrap">
                <img
                  :src="s.image"
                  :alt="s.name"
                  class="headshot"
                  @error="$event.target.style.display = 'none'" />
              </div>
              <div class="card-info">
                <p class="speaker-line">
                  <strong class="speaker-name">{{ s.name }}</strong>
                  {{ " " }}
                  <span class="affiliation" :style="s.affiliationStyle">{{ s.affiliation }}</span>
                </p>
                <p class="talk-title">{{ s.talk }}</p>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script setup>
import Email from "@/components/Email.vue";
import {computed, ref, onMounted} from "vue";
import {registrationUrl, redirectToRegistration} from "@/composables/useRedirectToRegistration";
import useVideoAccess from "@/composables/useVideoAccess";
import useEventState from "@/composables/useEventState";
import useSpeakerDialog from "@/composables/useSpeakerDialog";
import {getFullstackUrl} from "@/composables/useFullstackUrl";
import {fetchProgramSheet} from "@/composables/useProgramSheet";
const {isFullyPostEvent} = useEventState();
const {openSpeaker} = useSpeakerDialog();
const FULLSTACK = getFullstackUrl();

const YEARS = [2022, 2023, 2024, 2025, 2026];
const currentYear = Math.max(...YEARS);

// none, partial (one collection) or full; an expired year counts as locked (useVideoAccess.js)
const {hasWorkshopAccess, hasMasterclassAccess} = useVideoAccess();
const showValueSection = computed(() => !hasWorkshopAccess.value || !hasMasterclassAccess.value);
const MASTERCLASS_YEARS = new Set(YEARS);
const VIZBI_YEARS = new Set([2024, 2025, 2026]);

const SESSION_DEFS = [
  {
    key: "Masterclass",
    title: "DataVis Masterclass",
    years: MASTERCLASS_YEARS,
    countLabel: "masterclasses"
  },
  {
    key: "Keynote",
    title: "VIZBI Keynotes",
    years: VIZBI_YEARS,
    countLabel: "keynote presentations"
  },
  {
    key: "DNA",
    title: "Visualizing DNA",
    years: VIZBI_YEARS,
    bio: true,
    countLabel: "plenary presentations"
  },
  {
    key: "RNA",
    title: "Visualizing RNA",
    years: VIZBI_YEARS,
    bio: true,
    countLabel: "plenary presentations"
  },
  {
    key: "Proteins",
    title: "Visualizing Proteins",
    years: VIZBI_YEARS,
    bio: true,
    countLabel: "plenary presentations"
  },
  {
    key: "Cells",
    title: "Visualizing Cells",
    years: VIZBI_YEARS,
    bio: true,
    countLabel: "plenary presentations"
  },
  {
    key: "Tissues",
    title: "Visualizing Tissues",
    years: VIZBI_YEARS,
    bio: true,
    countLabel: "plenary presentations"
  },
  {
    key: "Ecosystems",
    title: "Visualizing Ecosystems",
    years: VIZBI_YEARS,
    bio: true,
    countLabel: "plenary presentations"
  }
];

const CATEGORY_MAP = {
  "Cellular Systems": "Cells",
  "Tissues & Organisms": "Tissues",
  "Populations & Ecosystems": "Ecosystems",
  Protein: "Proteins"
};

const EXCLUDE_CATEGORIES = new Set(["Showcase"]);

function normalizeCategory(cat) {
  return CATEGORY_MAP[cat] || cat;
}

function stripHtml(str) {
  const el = document.createElement("div");
  el.innerHTML = str;
  return (el.textContent || "").replace(/\u00AD/g, "");
}

// the folded name, masterclassTalks.json's key (as speakers.json)
const fold = (name) => name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

function portrait(name, year) {
  return `/events/${year}/people/images/portraits/${name.replace(/ /g, "_")}.jpg`;
}

function parsePeople(data, year) {
  const [headers, ...rows] = data;
  const idx = Object.fromEntries(headers.map((h, i) => [h, i]));
  return rows
    .map((row) => ({
      name: (row[idx["Name"]] || "").trim(),
      category: normalizeCategory((row[idx["Category"]] || "").trim()),
      talk: stripHtml((row[idx["Talk/Role/Event"]] || "").trim()),
      affiliation: (row[idx["Affiliation"]] || "").trim(),
      year
    }))
    .filter((s) => s.name && !s.name.startsWith("T.B.A") && !EXCLUDE_CATEGORIES.has(s.category));
}

// What each registration unlocks ({videos, hours}): computed once from the video index by
// fullstack (server/services/videos/buildVideoStats.js), the same figures #register shows.
// null (figures hidden) when stats.json can't be loaded.

const loading = ref(true);
const sessions = ref([]);
const tweaks = ref({text: {}, styles: {}});
const masterclassStats = ref(null);
const workshopStats = ref(null);
const libraryStats = ref(null);
const freeStats = ref(null);
const showInPersonBullet = ref(false);

function parseCloseDate(closeStr, year) {
  if (!closeStr) return null;
  const date = new Date(`${closeStr} ${year} 23:59:59`);
  return isNaN(date.getTime()) ? null : date;
}

function hasUpcomingInPerson(events) {
  const now = Date.now();
  for (const [name, event] of Object.entries(events)) {
    const yearMatch = name.match(/\d{4}/);
    const year = yearMatch ? parseInt(yearMatch[0]) : currentYear;
    const closeDate = parseCloseDate(event["in-person"]?.logistics?.RegistrationCloses, year);
    if (closeDate && closeDate.getTime() > now) return true;
  }
  return false;
}

function applyTextTweaks(str) {
  return tweaks.value.text[str] ?? str;
}

function affiliationStyle(affiliation) {
  return tweaks.value.styles[affiliation] ?? {};
}

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}`);
  return res.json();
}

onMounted(async () => {
  const events = await fetchJson(`${FULLSTACK}/${currentYear}/events.json`).catch(() => ({}));
  showInPersonBullet.value = hasUpcomingInPerson(events);
  const [results, stars, rawTweaks, videoStats, masterclassTalks] = await Promise.all([
    Promise.all(
      YEARS.map((year) =>
        fetchProgramSheet(year).then(({speakers}) => (speakers ? {year, data: speakers} : null))
      )
    ).then((results) => results.filter(Boolean)),
    fetchJson("/posters/stars/stars.json").catch(() => ({})),
    fetchJson("/posters/stars/tweaks.json").catch(() => ({text: {}, styles: {}})),
    fetchJson("/sitemaps/videos/stats.json").catch(() => ({})),
    // one title per Masterclass speaker, as the posters (Seán, 2026-10-31)
    fetchJson("/sitemaps/videos/masterclassTalks.json").catch(() => ({}))
  ]);
  tweaks.value = {text: rawTweaks.text ?? {}, styles: rawTweaks.styles ?? {}};
  // the Masterclass cards: the one title per speaker the posters show
  const getMasterclassTalk = (key, name) => key === "Masterclass" && masterclassTalks[fold(name)];

  masterclassStats.value = videoStats.masterclass || null;
  workshopStats.value = videoStats.workshop || null;
  libraryStats.value = videoStats.library || null;
  freeStats.value = videoStats.free || null;

  // the videos registration unlocks, per tag (stats.json tags, built with the hours)
  const videoCounts = videoStats.tags || {};

  results.sort((a, b) => b.year - a.year);
  const all = results.flatMap(({year, data}) => parsePeople(data, year));

  sessions.value = SESSION_DEFS.map((def) => {
    const seen = new Set();
    const speakers = all
      .filter((s) => s.category === def.key && def.years.has(s.year))
      .filter((s) => {
        if (seen.has(s.name)) return false;
        seen.add(s.name);
        return true;
      });
    const starNames = new Set(stars[def.key] || []);
    const starOrder = stars[def.key] || [];
    const featured = speakers
      .filter((s) => starNames.has(s.name))
      .sort((a, b) => starOrder.indexOf(a.name) - starOrder.indexOf(b.name))
      .map((s) => ({
        ...s,
        image: portrait(s.name, s.year),
        talk: getMasterclassTalk(def.key, s.name) || applyTextTweaks(s.talk),
        affiliation: applyTextTweaks(s.affiliation),
        affiliationStyle: affiliationStyle(s.affiliation)
      }));
    return {
      key: def.key,
      title: def.title,
      bio: def.bio ?? false,
      countLabel: def.countLabel,
      total: videoCounts[def.key] ?? 0, // 0 (count hidden) when stats.json has no tags
      featured
    };
  });

  loading.value = false;
});
</script>

<style scoped>
.unlock-videos {
  position: relative;
  background: black;
  color: white;
  font-family: var(--font-family);
  /* sized from the page column (body max-width 56rem, main's padding 2 × 1rem), not the window:
     with a smaller default font (e.g. a browser set to 12px) the column narrows too */
  font-size: clamp(5px, calc((min(100vw, 56rem) - 2rem) / 42.6), 20px);
  box-sizing: border-box;
  padding: 2em 0;
  margin: 0 auto 1.4em auto;
  width: 42.6em;
}

.unlock-content {
  padding: 0 2em;
}
/* narrow screens: no side padding inside the block (the page's own black margin is enough), and
   everything scaled up in proportion so the content (42.6em less the 2 × 2em padding) fills the
   same width the padded block did */
@media (max-width: 600px) {
  .unlock-videos {
    font-size: calc((100vw - 32px) / 38.6);
    width: 38.6em;
  }
  .unlock-content {
    padding-inline: 0;
  }
}

.poster-title {
  font-size: 2.82em;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
  color: var(--brand-color);
  letter-spacing: 0.04em;
  margin: 0 0 0.18em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}

.vizbi-logo {
  height: 2.7em;
  width: auto;
  transform: translateY(-6%);
}

.poster-subtitle {
  font-size: 1.31em;
  font-weight: 700;
  text-transform: uppercase;
  color: white;
  letter-spacing: 0.06em;
  margin: 0 0 0.76em;
}

:is(.text, .intro-list) {
  font-size: 1.32em;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 0.95em;
}

.text {
  line-height: 1.5;
  margin: 0 0 0.57em;
  max-width: 60ch;
}

.final {
  margin-bottom: 1.5em;
}

.intro-list {
  line-height: 1.4;
  margin: 0 0 1em;
  padding-left: 0;
  list-style: disc;
  list-style-position: inside;
}

:is(.value-prop, .highlights-label) {
  font-size: 1.53em;
  color: white;
}

.value-prop {
  letter-spacing: -0.03em;
  margin: 0 0 0.98em;
}

.highlights-label {
  margin: 0 0 0.49em;
}

.reg-buttons {
  display: flex;
  flex-direction: column;
  gap: 1.7em;
  margin: 0 2em 2.8em;
}

.reg-btn {
  background: var(--brand-hover-color);
  color: white;
  text-decoration: none;
  text-align: center;
  padding: 0.42em 1.15em;
  font-size: 1.3em;
  font-weight: 400;
  border-radius: 9999px;
}

.reg-btn strong {
  font-weight: 700;
}

.reg-buttons:has(+ .info-rows) {
  margin-bottom: 2.6em; /* ▶ EDIT HERE: the green buttons → the rows below them */
}

/* information, not actions (2026-10-14): full-width rows, alike, quieter than the green buttons;
   each row is one link */
.info-rows {
  display: flex;
  flex-wrap: wrap; /* side by side (2026-10-14); stacked where both don't fit (phones) */
  gap: 0.5em;
  margin: 0 0 2.8em; /* the text's full width, not the buttons' (2em in from each side) */
}

.info-row {
  /* equal widths, each text on one line: where both don't fit side by side, they stack */
  flex: 1 1 0;
  min-width: max-content;
  white-space: nowrap;
  display: block;
  text-align: center;
  padding: 0.65em 0.9em;
  font-size: 1em; /* ▶ EDIT HERE: the rows' text (one line each) */
  color: #e6e6e6;
  text-decoration: none;
  background: #1b1b1b;
  border: 1px solid #333;
  border-radius: 10px;
}

.info-row:hover {
  background: #262626;
  text-decoration: none;
}

.info-row:focus-visible {
  outline: 2px solid var(--brand-hover-color);
  outline-offset: 2px;
}



.session {
  margin-bottom: 1.5em;
}

.session-title {
  font-size: 1.635em;
  font-weight: 900;
  text-transform: uppercase;
  color: var(--brand-color);
  margin: 0 0 0.24em;
  letter-spacing: 0.02em;
  display: flex;
  align-items: baseline;
  gap: 0.5em;
  flex-wrap: wrap;
}

.session.biological .session-title {
  font-size: 1.35em;
}

.session-count {
  font-size: 0.8em;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: rgba(255, 255, 255, 0.5);
}

.biological .session-count {
  font-size: 0.85em;
}

.cards {
  display: flex;
  gap: 0.75em;
}

.card {
  flex: 1;
  min-width: 0;
}

.card-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.image-wrap {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: black;
  overflow: hidden;
}

.headshot {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.card-info {
  padding-top: 0.4em;
  font-size: 1em;
  line-height: 1.2;
}

.speaker-line {
  margin: 0;
}

.speaker-name {
  font-weight: 700;
  color: white;
}

.affiliation {
  color: rgba(255, 255, 255, 0.55);
}

.talk-title {
  margin: 0;
  color: var(--brand-color);
}

.loading {
  color: var(--brand-color);
  padding: 3em;
}
</style>
